import dayjs from 'dayjs';
import {
  fromEnumAsString,
  fromEnumAsStringOrNull,
  toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import {
  cloneData,
  cloneDataOrNull,
  DataModel,
  DataModelBuilder,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { Language } from './language.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { ComponentList } from '../../components/core/component-list.js';
import { BlacklistComponent } from '../../components/customer-profile/blacklist-component.js';
import { CustomNameComponent } from '../../components/customer-profile/custom-name-component.js';
import { OwnersComponent } from '../../components/customer-profile/owners-component.js';
import { StaffNoteComponent } from '../../components/notes/staff-note-component.js';
import { ClientInviteComponent } from '../../components/customer-profile/client-invite-component.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { DayOfYear } from './day-of-year.js';
import { EntityMixin } from '../../components/core/entity-mixin.js';
import { SearchUtils } from '../../utility/search-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';

export enum CustomerProfileStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class CustomerProfile extends EntityMixin(DataModel) implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = 'df8997d8cb804ac0809d1973ea69aa0c';
  static readonly SCHEMA_VERSION: number = 11;

  public readonly formattedName: string | null;
  public readonly initials: string | null;

  private constructor(
    public readonly uniqueId: string,
    public readonly firstName: string | null,
    public readonly lastName: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly isOnlineCustomer: boolean,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: CustomerProfileStatus = CustomerProfileStatus.Live,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly email: string | null,
    public readonly photo: PhotoReference | null,
    public readonly pushNotificationToken: string | null,
    public readonly userId: string | null,
    public readonly language: Language | null,
    public readonly components: ComponentList,
    public readonly dayOfBirth: DayOfYear | null,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName);
    this.initials = getInitialsFromName(firstName, lastName);
  }

  get isLive(): boolean {
    return this.status === CustomerProfileStatus.Live;
  }

  get isArchived(): boolean {
    return this.status === CustomerProfileStatus.Archived;
  }

  get $typeId(): string {
    return CustomerProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(10, CustomerProfile.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    firstName: string | null,
    lastName: string | null,
    creationDate: dayjs.Dayjs,
    isOnlineCustomer: boolean,
    businessRef: BusinessModuleRef,
    phoneNumber: PhoneNumber | null,
    email: string | null,
    photo: PhotoReference | null,
    pushNotificationToken: string | null,
    userId: string | null,
    language: Language | null,
    components: ComponentList,
    dayOfBirth: DayOfYear | null,
    status?: CustomerProfileStatus
  }) {
    return new CustomerProfile(
        opts.uniqueId,
        opts.firstName,
        opts.lastName,
        opts.creationDate,
        opts.isOnlineCustomer,
        opts.businessRef,
        opts.status ?? CustomerProfileStatus.Live,
        opts.phoneNumber,
        opts.email,
        opts.photo,
        opts.pushNotificationToken,
        opts.userId,
        opts.language,
        opts.components,
        opts.dayOfBirth,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): CustomerProfile {
    return new CustomerProfile(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'firstName'),
        MapUtils.getOrNull<string>(map, 'lastName'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<boolean>(map, 'isOnlineCustomer'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), CustomerProfileStatus),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        MapUtils.getOrNull<string>(map, 'email'),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'photo')),
        MapUtils.getOrNull<string>(map, 'pushNotificationToken'),
        MapUtils.getOrNull<string>(map, 'userId'),
        toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'language'), Language),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        context.fromDataOrNull<DayOfYear>(MapUtils.getOrNull<SerializedData>(map, 'dayOfBirth')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'firstName': this.firstName,
      'lastName': this.lastName,
      'creationDate': context.encodeDate(this.creationDate),
      'isOnlineCustomer': this.isOnlineCustomer,
      'businessRef': this.businessRef.encode(),
      '$status': fromEnumAsString(this.status),
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'email': this.email,
      'photo': context.toDataOrNull(this.photo),
      'pushNotificationToken': this.pushNotificationToken,
      'userId': this.userId,
      'language': fromEnumAsStringOrNull(this.language),
      'components': this.components.serialize(context),
      'dayOfBirth': context.toDataOrNull(this.dayOfBirth),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: CustomerProfile): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        other.creationDate === this.creationDate &&
        other.isOnlineCustomer === this.isOnlineCustomer &&
        isSameData(other.businessRef, this.businessRef) &&
        other.status === this.status &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        other.email === this.email &&
        isSameData(other.photo, this.photo) &&
        isSameData(other.dayOfBirth, this.dayOfBirth) &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.components.isSame(this.components);
  }

  copyWith({
    uniqueId = this.uniqueId,
    firstName = this.firstName,
    lastName = this.lastName,
    creationDate = TimeZone.cloneDate(this.creationDate),
    isOnlineCustomer = this.isOnlineCustomer,
    businessRef = this.businessRef.clone(),
    status = this.status,
    phoneNumber = cloneDataOrNull(this.phoneNumber),
    email= this.email,
    photo= cloneDataOrNull(this.photo),
    pushNotificationToken = this.pushNotificationToken,
    userId = this.userId,
    language = this.language,
    components = cloneData(this.components),
    dayOfBirth = this.dayOfBirth?.clone() ?? null,
  }: {
    uniqueId?: string,
    firstName?: string | null,
    lastName?: string | null,
    creationDate?: dayjs.Dayjs,
    isOnlineCustomer?: boolean,
    businessRef?: BusinessModuleRef,
    status?: CustomerProfileStatus,
    phoneNumber?: PhoneNumber | null,
    email?: string | null,
    photo?: PhotoReference | null,
    pushNotificationToken?: string | null,
    userId?: string | null,
    language?: Language | null,
    components?: ComponentList,
    dayOfBirth?: DayOfYear | null,
  } = {}): this {
    return new CustomerProfile(
        uniqueId,
        firstName,
        lastName,
        creationDate,
        isOnlineCustomer,
        businessRef,
        status,
        phoneNumber,
        email,
        photo,
        pushNotificationToken,
        userId,
        language,
        components,
        dayOfBirth,
    ) as this;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return this.copyWith({
      businessRef: businessRef,
      components: this.components.copyWithBusinessRef(businessRef),
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      userId: account.uniqueId,
    });
  }

  archived(): CustomerProfile {
    return this.copyWith({
      status: CustomerProfileStatus.Archived,
    });
  }

  alive(): CustomerProfile {
    return this.copyWith({
      status: CustomerProfileStatus.Live,
    });
  }

  blacklisted(): CustomerProfile {
    const components = this.components.clone();
    components.addOrReplaceComponent(new BlacklistComponent(true));

    return this.copyWith({
      components: components,
    });
  }

  // EXTENSION METHODS
  get hasJoinedByInvitation(): boolean {
    return this.components.hasComponentByTypeId(ClientInviteComponent.TYPE_ID);
  }

  get isBlacklisted(): boolean {
    const component = this.findComponentByTypeId<BlacklistComponent>(BlacklistComponent.TYPE_ID);
    return component?.isBlacklisted === true;
  }

  getOwners(): string[] {
    const component = this.findComponentByTypeId<OwnersComponent>(OwnersComponent.TYPE_ID);
    return component?.owners ?? [];
  }

  static encodedStatusSafe(status: CustomerProfileStatus): string {
    return fromEnumAsString(status);
  }

  getQueryableFields(): string[] {
    return [
      this.uniqueId,
      this.userId,
      this.firstName,
      this.lastName,
      this.email,
    ].filter(isNotNullOrUndefined);
  }

  generateSearchQueryArray() : string[] {
    return SearchUtils.generateSearchQueryArray(
        this.getQueryableFields(),
    );
  }
}

export class CustomerProfileBuilder extends DataModelBuilder<CustomerProfile> {
  build(context: SerializationContext, map: SerializedData): CustomerProfile {
    return CustomerProfile.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: CustomerProfile): SerializedData | null {
    return {
      'search': model.generateSearchQueryArray(),
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    MapUtils.replace(
        map, '$status', 'DELETED',
        fromEnumAsString(CustomerProfileStatus.Archived),
    );

    if(versionRange.contains(CustomerProfile.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CustomerProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // In version 2 the [$status] field was introduces.
    if(version < 2) {
      MapUtils.set(map, '$status', CustomerProfileStatus.Live);
      version = 2;
    }

    // Version 3 introduces [overrideFirstName] and [overrideLastName].
    // The default value for these is [null].
    if(version < 3) {
      version = 3;
    }

    // Version 4 renames [joinDate] to [creationDate].
    if(version < 4) {
      MapUtils.set(map, 'creationDate', MapUtils.get(map, 'joinDate'));
      version = 4;
    }

    // Version 5 reintroduces [joinDate] but now meaning "the time when the user used an invite code".
    // Previously it was equivalent to the [creationDate].
    if(version < 5) {
      // Will result in the migrated join date or [null], if the client hasn't been invited.
      MapUtils.setIfNullOrUndefined(map, 'joinDate', MapUtils.getOrNull(map, 'v5_joinDate'));
      version = 5;
    }

    // Version 6 introduces the [owners] list which contains staff profile IDs.
    if(version < 6) {
      MapUtils.setIfNullOrUndefined(map, 'owners', []);
      version = 6;
    }

    // In this version [businessId] was replaced with [businessRef].
    if(version < 7) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 7;
    }

    // Version 8 introduces the [isBlacklisted] flag.
    if(version < 8) {
      MapUtils.setIfNullOrUndefined(map, 'isBlacklisted', false);
      version = 8;
    }

    /*
        Version 9 introduces components and removes/replaces the following fields:
          REPLACED
          * isBlacklisted
          * overrideFirstName
          * overrideLastName
          * owners
          * hasJoinedByInvitation
          * joinDate
          * notes

          REMOVED
          * gender
          * birthday
          * allergies
          * address
          * discountPercentage
    */
    if(version < 9) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      const isBlacklisted = MapUtils.get<boolean>(map, 'isBlacklisted');
      const overrideFirstName = MapUtils.getOrNull<string>(map, 'overrideFirstName');
      const overrideLastName = MapUtils.getOrNull<string>(map, 'overrideLastName');
      const owners = MapUtils.getList<string>(map, 'owners', (e) => e as string);
      const notes = MapUtils.getOrNull<string>(map, 'notes');
      const creationDate = context.decodeDate(MapUtils.get<number>(map, 'creationDate'));
      const joinDate = context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'joinDate'));
      const hasJoinedByInvitation = MapUtils.getOrNull<boolean>(map, 'hasJoinedByInvitation') ?? false;

      components.addOrReplaceComponent(new BlacklistComponent(isBlacklisted));
      components.addOrReplaceComponent(new CustomNameComponent(overrideFirstName, overrideLastName));
      components.addOrReplaceComponent(new OwnersComponent(owners));
      components.addOrReplaceComponent(new StaffNoteComponent(notes ?? ''));

      if(hasJoinedByInvitation) {
        components.addOrReplaceComponent(new ClientInviteComponent(joinDate ?? creationDate, null));
      }

      map['components'] = components.serialize(context);
      version = 9;
    }

    // In this version the [isOnlineClient] field was renamed to [isOnlineCustomer]
    if(version < 10) {
      map['isOnlineCustomer'] = map['isOnlineClient'];
      version = 10;
    }

    // In this version the [dayOfBirth] field was added
    if(version < 11) {
      version = 11;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
